import { useState } from "react";
import { Link } from "react-router-dom";
import { usePositions } from "../hooks/positions.jsx";
import { dateLabel } from "../lib/format.js";
import CloseForm from "./CloseForm.jsx";
import Icon from "./ui/Icon.jsx";
import Modal from "./ui/Modal.jsx";
import { Delta, Money } from "./ui/Primitives.jsx";

const REASONS = { stop: "Trailing stop hit", deadline: "Holding deadline reached" };

/**
 * Pops up on every visit while any exit alert is pending. Closing it only
 * defers it to the next visit; recording the sale or ignoring clears it.
 */
export default function ExitAlertDialog() {
  const { pendingExits, close, ignoreAlert } = usePositions();
  const [later, setLater] = useState(false);
  const [closing, setClosing] = useState(null);
  const [ignoring, setIgnoring] = useState(null);
  const [error, setError] = useState(null);

  if (later || !pendingExits.length) return null;

  const dismiss = () => setLater(true);

  const ignore = async (id) => {
    setIgnoring(id);
    setError(null);
    try {
      await ignoreAlert(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setIgnoring(null);
    }
  };

  const count = pendingExits.length;

  return (
    <Modal label="Exit alert" onClose={dismiss} className="exit-alert-dialog">
      <div className="modal-body">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">Exit alert</p>
            <h2>Exit the below {count === 1 ? "stock" : `${count} stocks`}</h2>
          </div>
          <button type="button" className="icon-btn" aria-label="Remind me later" onClick={dismiss}><Icon name="close" /></button>
        </div>
        <p className="guide-intro">
          The model's exit rule was reached at the close. Record the sale once you have sold, or ignore the alert to keep holding.
        </p>

        <ul className="exit-alert-list">
          {pendingExits.map((p) => (
            <li key={p.id}>
              <div className="position-head">
                <div className="position-id">
                  <strong>{p.symbol}<span className="tag-exit">Exit now</span></strong>
                  <span>
                    {REASONS[p.exitReason] ?? "Model exit reached"} · after the {dateLabel(p.alert.session)} close
                    {p.modelExitPrice != null && <> · model exit <Money value={p.modelExitPrice} /></>}
                  </span>
                </div>
                <div className="position-value">
                  <Money value={p.currentPrice} />
                  <small><Delta value={p.returnPct} /></small>
                </div>
              </div>
              <div className="position-actions">
                <button type="button" className="btn sm primary" onClick={() => setClosing(p.id)}>Mark as exited</button>
                <button type="button" className="btn sm" disabled={ignoring === p.id} onClick={() => ignore(p.id)}>
                  {ignoring === p.id ? "Ignoring…" : "Ignore"}
                </button>
              </div>
              {closing === p.id && (
                <CloseForm position={p} onCancel={() => setClosing(null)} onDone={() => setClosing(null)} onClose={close} />
              )}
            </li>
          ))}
        </ul>

        {error && <p className="mark-entered-error" role="alert">{error}</p>}
        <p className="modal-footnote">
          Closing this window only hides it until your next visit. <Link to="/investments" onClick={dismiss}>Open My investments</Link>
        </p>
      </div>
    </Modal>
  );
}
